import PropTypes from "prop-types";
import React, {
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
    LoadingContext,
    M_StateActions,
    MTable,
} from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Tracking Shipment CRUD page in a tab.
 */
const TRACKING_CRUD_ROUTE = "/home/tracking/trackingConfigCrud";

//endregion

//region Helpers

/**
 * Normalizes role permissions received from the parent.
 *
 * Purpose:
 * - supports role objects with Value;
 * - supports plain string permissions;
 * - avoids passing undefined entries to MTable.
 *
 * @param {Array<object|string>|undefined} roles - Incoming roles.
 * @returns {Array<string>|undefined} Role permission values.
 */
const getRolePermissions = (roles) => {
    if (!Array.isArray(roles)) return undefined;

    return roles
        .map((role) => role?.Value ?? role)
        .filter(Boolean);
};

//endregion

//region Component

/**
 * Renders the Tracking Shipments configuration page.
 *
 * Responsibilities:
 * - render the common configuration-page layout;
 * - keep the page visually aligned with other Config pages through config-page.css;
 * - render the TrackingShipments MTable;
 * - expose only Create and View operations;
 * - replace default MTable CRUD actions with tab-based navigation.
 *
 * Important:
 * - this page is not rendered inside a tabs section;
 * - therefore it uses only config-page, without config-page--tabs;
 * - MTable uses "Update" internally for the Edit action, so "Update" must be removed.
 *
 * @param {object} props - Component props.
 * @param {Array<object|string>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @param {boolean} props.refresh - Optional external refresh flag.
 * @param {Function} props.setRefresh - Optional external refresh setter.
 * @returns {JSX.Element} Tracking Shipments configuration page.
 */
export default function TrackingConfig({
    roles,
    onCreateTab,
    toastMessages,
    refresh: externalRefresh,
    setRefresh: setExternalRefresh,
}) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [internalRefresh, setInternalRefresh] = useState(false);

    /**
     * Uses the parent refresh controller when available.
     *
     * Purpose:
     * - allows this config to work inside parent pages that own refresh state;
     * - still keeps the component self-contained when rendered standalone.
     */
    const refresh = externalRefresh ?? internalRefresh;
    const setRefresh = setExternalRefresh ?? setInternalRefresh;

    /**
     * Keeps table permissions stable and resilient to different role payload shapes.
     */
    const rolePermissions = useMemo(
        () => getRolePermissions(roles),
        [roles]
    );

    /**
     * Removes table actions that are not available in this screen.
     *
     * Business rule:
     * - Tracking Shipments allow only Create and View;
     * - Print, Copy, Edit/Update, Delete and Audit must not be exposed.
     */
    const removeContextItems = useMemo(
        () => ["Print", "Copy", "Update", "Delete", "Audit"],
        []
    );

    /**
     * Opens the Tracking Shipment CRUD tab for the requested operation.
     *
     * Flow:
     * - persists the operation in M_State;
     * - validates row selection for View;
     * - persists the selected TrackingShipment row when required;
     * - opens the configured CRUD route with a contextual tab label.
     *
     * @param {"Create"|"View"} operation - Requested operation.
     * @param {object|null|undefined} row - Selected tracking shipment row.
     * @returns {void}
     */
    const openTrackingShipmentCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (operation === "View") {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(
                    M_StateActions.addM_State({
                        key: "TrackingShipmentLine",
                        value: row,
                    })
                );
            }

            const tabLabel =
                operation === "Create"
                    ? t("MenuTrackingLabel")
                    : `${t("MenuTrackingLabel")} - ${t("View")}`;

            onCreateTab(TRACKING_CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the Tracking workflow.
     *
     * Only Create and View are intentionally exposed for this page.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openTrackingShipmentCrud("View", row)],
                ["Create", () => openTrackingShipmentCrud("Create")],
            ]),
        [openTrackingShipmentCrud]
    );

    return (
        <div
            className="config-page"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header">
                <div className="config-page__title">
                    {t("MenuTrackingLabel")}
                </div>

                <div className="config-page__description">
                    {t("InfoTracking")}
                </div>
            </div>

            <div className="config-page__table">
                <MTable
                    filtersOnTable
                    entityName="TrackingShipments"
                    roleName="Tracking"
                    rolePermissions={rolePermissions}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    replaceFunctions={replaceFunctions}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

TrackingConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
    refresh: PropTypes.bool,
    setRefresh: PropTypes.func,
};

//endregion